import type { PaginationState, SortingState } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface UseTableUrlStateOptions {
  /** Default pagination page size if `pageSize` is absent from the URL. */
  defaultPageSize?: number;
  /** Default sorting if `sortBy`/`sortDir` are absent from the URL. */
  defaultSorting?: SortingState;
  /**
   * Optional prefix for URL params so multiple tables on the same page can
   * each own independent state. With `prefix: 'users'` the keys become
   * `usersPage`, `usersPageSize`, `usersSortBy`, `usersSortDir`. Omit for the
   * default `page` / `pageSize` / `sortBy` / `sortDir`.
   */
  prefix?: string;
}

export interface UseTableUrlStateResult {
  sorting: SortingState;
  pagination: PaginationState;
  onSortingChange: (next: SortingState) => void;
  onPaginationChange: (next: PaginationState) => void;
}

/**
 * The URL key a table owns for one piece of state: `page` with no prefix,
 * `usersPage` with `prefix: 'users'`.
 *
 * Exported so sibling hooks that store *other* state under the same table
 * prefix (`useUsersTableFilters`'s `usersQ`, `usersCustomer`, ...) share this
 * one naming rule instead of re-deriving it.
 */
export function prefixedKey(prefix: string | undefined, base: string): string {
  if (!prefix) return base;
  return `${prefix}${base.charAt(0).toUpperCase()}${base.slice(1)}`;
}

/**
 * Backs a DataTable's sorting + pagination state with URL search params so the
 * state survives navigation (back/forward, deep links, refresh).
 *
 * URL shape: `?page=2&pageSize=25&sortBy=name&sortDir=desc`
 *  - `page` is 1-indexed in the URL; converted to 0-indexed for TanStack.
 *  - Default values (page 1, default size, no sort) are stripped so a clean
 *    list URL stays clean.
 *
 * For pages hosting more than one table, pass `prefix` to namespace the keys
 * (e.g. `prefix: 'users'` → `usersPage`, `usersPageSize`, ...).
 */
export function useTableUrlState(
  options: UseTableUrlStateOptions = {},
): UseTableUrlStateResult {
  const { defaultPageSize = 10, defaultSorting = [], prefix } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const pageKey = prefixedKey(prefix, 'page');
  const pageSizeKey = prefixedKey(prefix, 'pageSize');
  const sortByKey = prefixedKey(prefix, 'sortBy');
  const sortDirKey = prefixedKey(prefix, 'sortDir');

  const sorting = useMemo<SortingState>(() => {
    const sortBy = searchParams.get(sortByKey);
    if (!sortBy) return defaultSorting;
    const sortDir = searchParams.get(sortDirKey);
    return [{ id: sortBy, desc: sortDir === 'desc' }];
  }, [searchParams, defaultSorting, sortByKey, sortDirKey]);

  const pagination = useMemo<PaginationState>(() => {
    const pageRaw = Number(searchParams.get(pageKey));
    const pageSizeRaw = Number(searchParams.get(pageSizeKey));
    const pageIndex = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw - 1 : 0;
    const pageSize = Number.isFinite(pageSizeRaw) && pageSizeRaw > 0
      ? pageSizeRaw
      : defaultPageSize;
    return { pageIndex, pageSize };
  }, [searchParams, defaultPageSize, pageKey, pageSizeKey]);

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onSortingChange = useCallback(
    (next: SortingState) => {
      updateParams((params) => {
        const first = next[0];
        if (!first) {
          params.delete(sortByKey);
          params.delete(sortDirKey);
          return;
        }
        params.set(sortByKey, first.id);
        if (first.desc) {
          params.set(sortDirKey, 'desc');
        } else {
          params.delete(sortDirKey);
        }
      });
    },
    [updateParams, sortByKey, sortDirKey],
  );

  const onPaginationChange = useCallback(
    (next: PaginationState) => {
      updateParams((params) => {
        if (next.pageIndex === 0) {
          params.delete(pageKey);
        } else {
          params.set(pageKey, String(next.pageIndex + 1));
        }
        if (next.pageSize === defaultPageSize) {
          params.delete(pageSizeKey);
        } else {
          params.set(pageSizeKey, String(next.pageSize));
        }
      });
    },
    [updateParams, defaultPageSize, pageKey, pageSizeKey],
  );

  return { sorting, pagination, onSortingChange, onPaginationChange };
}
